import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { notesApi, type NoteSummary } from '@/api/notes';
import { errorMessage } from '@/api/client';
import Modal from '@/components/Modal';
import {
  base64ToBytes,
  bytesToBase64,
  decryptSym,
  encryptSym,
  generateDEK,
  unwrapKey,
  wrapKey,
  type KeyPair,
} from '@/crypto/sodium';
import libsodium from 'libsodium-wrappers-sumo';
import { formatDate } from '@/lib/format';

interface Props {
  tenantId: string;
  parentId: string | null;
  keyPair: KeyPair;
  onCreated: (newNoteId: string) => void;
  onClose: () => void;
}

/**
 * Modal care listează template-urile tenantului și, la selecție, creează
 * o notiță nouă cu conținutul copiat din template. Tot flow-ul e ZK:
 * clientul decriptează template-ul cu propriul keyPair, generează DEK nou,
 * criptează conținutul pt sine și trimite POST /notes.
 */
export default function TemplatePickerModal({
  tenantId,
  parentId,
  keyPair,
  onCreated,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['note-templates', tenantId],
    queryFn: () => notesApi.listTemplates(tenantId),
  });

  const createFromTemplate = useMutation({
    mutationFn: async (template: NoteSummary) => {
      setBusyId(template.id);
      // 1. Fetch detail template + decrypt conținut
      const detail = await notesApi.get(template.id);
      const oldDek = await unwrapKey(base64ToBytes(detail.dek_wrapped), keyPair);
      const plainBytes = await decryptSym(
        {
          ciphertext: base64ToBytes(detail.ciphertext),
          nonce: base64ToBytes(detail.nonce),
        },
        oldDek,
      );
      libsodium.memzero(oldDek);

      // 2. Generăm DEK nou + criptăm conținutul pentru notița nouă
      const newDek = await generateDEK();
      try {
        const enc = await encryptSym(plainBytes, newDek);
        const wrapped = await wrapKey(newDek, keyPair.publicKey);

        // 3. Titlu: „{template.title} (copie)" sau prompt scurt
        const newTitle = t('notes_ui.templates.copy_title', { title: template.title });

        const created = await notesApi.create(tenantId, {
          title: newTitle,
          parent_id: parentId,
          ciphertext: bytesToBase64(enc.ciphertext),
          nonce: bytesToBase64(enc.nonce),
          dek_wrapped: bytesToBase64(wrapped),
        });
        return created;
      } finally {
        libsodium.memzero(newDek);
      }
    },
    onSuccess: (created) => {
      setError(null);
      setBusyId(null);
      onCreated(created.id);
    },
    onError: (err) => {
      setBusyId(null);
      setError(errorMessage(err, t('notes_ui.templates.create_error')));
    },
  });

  const title = <h2 className="display-title-sm">{t('notes.choose_template')}</h2>;

  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-lg">
      <div className="space-y-3">
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {t('notes_ui.templates.description')}
        </p>

        {list.isLoading && (
          <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>
        )}

        {list.data && list.data.length === 0 && (
          <div
            className="text-sm text-center py-8 rounded"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
            }}
          >
            {t('notes_ui.templates.empty')}
            <div className="text-xs mt-2" style={{ color: 'var(--text-dim)' }}>
              {t('notes_ui.templates.empty_hint')}
            </div>
          </div>
        )}

        {list.data && list.data.length > 0 && (
          <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {list.data.map((tpl) => (
              <li key={tpl.id}>
                <button
                  type="button"
                  className="w-full text-left py-3 px-2 flex items-center justify-between hover:bg-[var(--bg-hover)] rounded transition-colors disabled:opacity-50"
                  onClick={() => createFromTemplate.mutate(tpl)}
                  disabled={busyId !== null}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{tpl.title}</div>
                    <div
                      className="text-[11px] font-mono mt-0.5"
                      style={{ color: 'var(--text-dim)' }}
                    >
                      {tpl.char_count}c · {formatDate(tpl.updated_at)}
                    </div>
                  </div>
                  {busyId === tpl.id ? (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {t('notes_ui.templates.creating')}
                    </span>
                  ) : (
                    <span
                      className="text-xs"
                      style={{ color: 'var(--accent)' }}
                    >
                      →
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <div
            className="text-xs rounded px-3 py-2"
            style={{
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={onClose}
            disabled={busyId !== null}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
